import { useCallback, useState } from 'react';
import isFunction from 'lodash/isFunction';

import { BaseStoreEntityType } from 'src/store/types';
import { BaseApiResponseErrorsType } from 'src/models/BaseApi';
import {
  convertPreparedApiErrorsToFormErrors,
  getErrorTextFromPreparedApiResponse,
  PreparedFormErrorsType,
} from 'src/utils/errors';
import { SafeAnyType } from 'src/utils/safeAny';
import { UseAnyRequestErrorsConfigType } from 'src/models/useAnyRequest';
import { useAnyRequest } from 'src/utils/hooks/useAnyRequest';

type UseAnyRequestErrorsStateReturnType<Response> = {
  response: BaseStoreEntityType<Response, BaseApiResponseErrorsType>;
  errorTextList: string[];
  formErrors: PreparedFormErrorsType;
  clearErrors: () => void;
  addErrors: (errors: string[]) => void;
};

/**
 * helper to save response errors
 *
 * @param uniqueId - prop of useAnyRequest hook {@see useAnyRequest}
 * @param onError - callback to handle beautiful response errors (optional)
 * @param config - rest props of useAnyRequest hook (optional) {@see useAnyRequest}
 */
export const useAnyRequestErrorsState = <Response = SafeAnyType, Data = SafeAnyType>(
  uniqueId: string,
  { onError, ...config }: UseAnyRequestErrorsConfigType<Response, Data> = {}
): UseAnyRequestErrorsStateReturnType<Response> => {
  const [errorTextList, setErrorTextList] = useState<string[]>([]);
  const [formErrors, setFormErrors] = useState<PreparedFormErrorsType>([]);

  const clearErrors = useCallback(() => {
    setErrorTextList([]);
  }, []);

  const handleError = useCallback(
    (responseErrors: BaseApiResponseErrorsType) => {
      const errors = {
        errorMessages: getErrorTextFromPreparedApiResponse(responseErrors),
        errorsByProperty: convertPreparedApiErrorsToFormErrors(responseErrors),
        errors: responseErrors,
      };

      if (isFunction(onError)) {
        onError(errors);
      }
      setErrorTextList(errors.errorMessages);
      setFormErrors(errors.errorsByProperty);
    },
    [onError]
  );

  const addErrors = useCallback((errors: string[]) => {
    setErrorTextList((prevState) => [...new Set([...prevState, ...errors])]);
  }, []);

  const response = useAnyRequest<Response, BaseApiResponseErrorsType, Data>(uniqueId, {
    onError: handleError,
    ...config,
  });

  return { response, errorTextList, formErrors, clearErrors, addErrors };
};
